import {
    IAuthenticationManager,
    IAuthManagerArguments,
    IAuthManagerStatus,
} from '../interface/auth';
import { IFetcher } from '../../interface/util';
import { IAuthAPI } from '../../api/interface';

interface IAuthManagerPrv extends IAuthenticationManager {
    pendingRenew: Promise<string> | null;
}

const TIME_REFRESH_THRESHOLD = 30 * 1000; /* 30s */

interface IAuthManagerSaveState {
    accessToken: string;
    expiry: number;
    apiUrl: string;
    apiExtension: string;
    clientId: string;
    clientSecret: string;
    grantType: string;
    username: string;
    password: string;
}

function _open(authAPI: IAuthAPI, initState: IAuthManagerSaveState): IAuthenticationManager {
    const manager: IAuthManagerPrv = {
        pendingRenew: null,

        accessToken: initState.accessToken,
        expiry: new Date(initState.expiry * 1000),

        apiUrl: initState.apiUrl,
        apiExtension: initState.apiExtension,
        clientId: initState.clientId,
        clientSecret: initState.clientSecret,
        grantType: initState.grantType,
        username: initState.username,
        password: initState.password,

        status: function (this: IAuthManagerPrv): IAuthManagerStatus {
            if (this.accessToken.length <= 1) {
                return 'Unset';
            }

            if (this.hasExpired()) {
                return 'Expired';
            }

            return 'Authorized';
        },

        hasExpired: function (this: IAuthManagerPrv): boolean {
            const now = new Date().getTime();
            const threshold =
                now - TIME_REFRESH_THRESHOLD; /* if we are within 30s, get a new token */
            const expiry = this.expiry.getTime();
            const hasExpired = expiry < threshold;
            return hasExpired;
        },

        renew: function (this: IAuthManagerPrv): Promise<string> {
            const manager = this;

            if (!manager.hasExpired()) {
                return Promise.resolve(manager.accessToken);
            }

            if (!manager.pendingRenew) {
                manager.pendingRenew = new Promise(async function (resolve, reject) {
                    authAPI
                        .authenticate(manager.apiUrl, manager.username, manager.password)
                        .then(function (authResponse) {
                            manager.accessToken = authResponse.access_token;
                            const expiry = new Date();
                            expiry.setSeconds(expiry.getSeconds() + authResponse.expires_in);
                            manager.expiry = expiry;
                            resolve(authResponse.access_token);
                            manager.pendingRenew = null;
                        })
                        .catch(error =>
                            reject(
                                `Authentication failed: ${JSON.stringify(error).substring(0, 30)}`,
                            ),
                        );
                });
            }

            return manager.pendingRenew;
        },

        serialize: function (this: IAuthManagerPrv): string {
            const obj: IAuthManagerSaveState = {
                accessToken: this.accessToken,
                expiry: this.expiry.getSeconds(),
                apiUrl: this.apiUrl,
                apiExtension: this.apiExtension,
                clientId: this.clientId,
                clientSecret: this.clientSecret,
                grantType: this.grantType,
                username: this.username,
                password: this.password,
            };

            return JSON.stringify(obj);
        },
    };

    return manager as IAuthenticationManager;
}

function open(authAPI: IAuthAPI, args: IAuthManagerArguments): IAuthenticationManager {
    const state: IAuthManagerSaveState = {
        /* make sure onCreation #hasExpired() returns true */
        expiry: new Date().getSeconds() - TIME_REFRESH_THRESHOLD - 1,
        accessToken: '',
        ...args,
    };

    return _open(authAPI, state);
}

function expand(authAPI: IAuthAPI, initString: string): IAuthenticationManager {
    const initState: IAuthManagerSaveState = initString.length > 2 ? JSON.parse(initString) : {};
    return _open(authAPI, initState);
}

const module = {
    open,
    expand,
};

export default module;
